import { SystemServiceCode } from "../stores";
import { https } from "./https";

export type BookingPartnerServiceFee = {
  feeValue: number;
  category: "WALK_IN" | "ADVANCE_BOOKING" | string;
  amountType: "FIXED_FEE" | string;
};

export type BookingSystemService = {
  id: number;
  name: string;
  code: SystemServiceCode;
};

export type BookingPartnerService = {
  id: number;
  status: "ENABLED" | "DISABLED" | string;
  partnerId: number;
  serviceId: number;
  service: BookingSystemService;
  fees: BookingPartnerServiceFee[];
};

export type BookingPartnerLocationCity = {
  id: number;
  name: string;
};

export enum NearbyPlaceType {
  transitStation = "transit_station",
  busStation = "bus_station",
  busStop = "bus_stop",
  parkingLot = "parking_lot",
  trainStation = "train_station",
}

export type BookingPartnerLocation = {
  id: number;
  distance: number;
  name: string;
  code: string;
  phoneNumber: string | null;
  emailAddress: string | null;
  postalCode: string | null;
  longitude: number | null;
  lattitude: number | null;
  streetAddress: string | null;
  customerGuidelines: string | null;
  walkInGuidelines: string | null;
  teleConsultationGuidelines: string | null;
  imageUrl: string | null;
  status: "ENABLED" | "DISABLED" | string;
  bookingNoticeMinutes: number | null;
  isBookingNoticeEnabled: boolean | null;
  cityId: number | null;
  partnerId: number | null;
  timeZone: string | null;
  isAvailableNow: boolean | null;
  createdAt: string;
  modifiedAt: string;
  createdById: number | null;
  modifiedById: number | null;
  partner?: {
    id: number;
    usesExternalCalendar: boolean;
  };
  city?: BookingPartnerLocationCity;
  availableServices?: string[];
  nearbyPlaces?:
    | { name: string; type: NearbyPlaceType; distance: number }[]
    | null;
};

export type Product = {
  id: number;
  name: string;
  code: string;
  imageUrl: string | null;
  price: number;
  totalAdvanceDiscountPrice?: number | null;
  totalAdvancePrice: number;
  totalWalkInPrice: number;
  pricing?: GlobalServicePricing;
  slotDuration: number;
  description: string | null;
  guidelines: string | null;
  allowAllLocations: boolean;
  status: "ENABLED" | "DISABLED" | string;
  telehealth?: boolean;
  partnerId?: number;
  partnerServiceId: number;
  createdAt: string;
  modifiedAt: string;
  createdById: number | null;
  modifiedById: number | null;
  partner_service: BookingPartnerService;
  product?: number;
  location?: number;
  partner_location?: BookingPartnerLocation;
  slots?: string[];
};

export enum ProductsParamsSortBy {
  availability = "availability",
  price = "price",
  distance = "distance",
}

export type ProductsParams = {
  page?: number;
  pageSize?: number;
  serviceTypeId?: number | string;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: ProductsParamsSortBy;
  latitude?: number;
  longitude?: number;
};

export type PartnerProductsPayload = {
  pages: number;
  count: number;
  currentPage: number;
  pageSize: number;
  results: Product[];
};

export type ProductsResponse = {
  success: boolean;
  data: {
    partnerProducts: PartnerProductsPayload;
    subscriptionDetails: {
      isSubscribedUser: boolean;
      isFree: boolean;
      chip: string | null;
    };
  };
  message: string;
  error: unknown;
};

export type GlobalServiceQueryParams = {
  code?: string;
  name?: string;
  latitude?: string;
  longitude?: string;
  countryCode?: string;
};

export type GlobalServicePricing = {
  free: boolean;
  price: number;
  currency: string;
};

export type GlobalServiceDetails = {
  serviceName: string;
  description: string;
  userDescription: string;
  callToAction: string;
  chip: string | null;
  availability: string;
  language: string;
  duration: string;
  pricing: GlobalServicePricing;
  features: string[];
};

export type GlobalServiceResponse = {
  success: boolean;
  data: {
    service: GlobalServiceDetails;
  } | null;
  message: string;
  error: unknown;
};

export async function products(params: ProductsParams) {
  const { data } = await https.get<any>("/partnerProduct/tpa/products", {
    params,
  });

  data.data.partnerProducts.results = data.data.partnerProducts.results.flatMap(
    (p: any) => {
      const locs = p?.partner_product_locations;
      if (!Array.isArray(locs) || !locs.length) return [p];
      const { partner_product_locations: _ignored, ...rest } = p;
      return locs.map((loc: any) => ({ ...rest, ...loc }));
    },
  );

  // Transform the available services object to an array of service types
  data.data.partnerProducts.results.forEach((row: any) => {
    const services = row?.partner_location?.availableServices;
    if (!services || Array.isArray(services)) return;
    row.partner_location.availableServices = Object.entries(services)
      .filter(([_, value]) => value)
      .map(([key]) => key);
  });

  return data;
}

export async function globalService(params: GlobalServiceQueryParams) {
  const { data } = await https.get<GlobalServiceResponse>("/global/services", {
    params,
  });

  return data;
}
